// Short, localized card location from Bókun/Google-style addresses
// ("National Reef Park Of Puerto Morelos, Rafael E. Melgar, Puerto Morelos, Quintana Roo, Mexico"
// → "Puerto Morelos, Quintana Roo"). Points of interest and streets (often in
// English) are dropped; known place names are localized; nothing is invented.
type Lang = "es" | "en" | "fr" | "it" | "de" | "pt";

const COUNTRIES = new Set(["mexico", "méxico", "belize", "guatemala", "el salvador"]);

const NAMES: Record<string, Partial<Record<Lang, string>> & { es: string }> = {
  "mexico": { es: "México", en: "Mexico", fr: "Mexique", it: "Messico", de: "Mexiko", pt: "México" },
  "méxico": { es: "México", en: "Mexico", fr: "Mexique", it: "Messico", de: "Mexiko", pt: "México" },
  "mexico city": { es: "Ciudad de México", en: "Mexico City", fr: "Mexico", it: "Città del Messico", de: "Mexiko-Stadt", pt: "Cidade do México" },
  "ciudad de méxico": { es: "Ciudad de México", en: "Mexico City", fr: "Mexico", it: "Città del Messico", de: "Mexiko-Stadt", pt: "Cidade do México" },
  "state of mexico": { es: "Estado de México", en: "State of Mexico", fr: "État de Mexico", it: "Stato del Messico", de: "Bundesstaat México", pt: "Estado do México" },
  "estado de méxico": { es: "Estado de México", en: "State of Mexico", fr: "État de Mexico", it: "Stato del Messico", de: "Bundesstaat México", pt: "Estado do México" },
  "belize": { es: "Belice", en: "Belize", fr: "Belize", it: "Belize", de: "Belize", pt: "Belize" },
  "belize city": { es: "Ciudad de Belice", en: "Belize City", fr: "Belize City", it: "Belize City", de: "Belize City", pt: "Cidade de Belize" },
  "guatemala city": { es: "Ciudad de Guatemala", en: "Guatemala City", fr: "Guatemala Ciudad", it: "Città del Guatemala", de: "Guatemala-Stadt", pt: "Cidade da Guatemala" },
  "yucatan": { es: "Yucatán" }, "merida": { es: "Mérida" }, "san luis potosi": { es: "San Luis Potosí" },
  "chis.": { es: "Chiapas" }, "ver.": { es: "Veracruz" }, "cdmx": { es: "CDMX" },
};

const fix = (s: string, lang: Lang) => { const n = NAMES[s.toLowerCase()]; return n ? (n[lang] ?? n.es) : s; };

export function locationLabel(raw: string | null | undefined, lang: string): string | null {
  const l = (["es", "en", "fr", "it", "de", "pt"].includes(lang) ? lang : "es") as Lang;
  const parts = String(raw ?? "").split(",").map(s => s.trim()).filter(Boolean);
  if (!parts.length) return null;
  let country: string | null = null;
  if (parts.length > 1 && COUNTRIES.has(parts[parts.length - 1].toLowerCase())) country = parts.pop()!;
  // City + state (last two remaining parts); a lone place stays as is.
  let keep = parts.slice(-2);
  if (keep.length === 2 && keep[0].toLowerCase() === keep[1].toLowerCase()) keep = [keep[1]];
  const out = keep.map(p => fix(p, l));
  // Mexico is implied on a Mexican travel site; other countries stay visible.
  if (country && !/^m[eé]xico$/i.test(country)) out.push(fix(country, l));
  else if (country && keep.length === 1 && /^m[eé]xico$/i.test(keep[0])) return fix(keep[0], l);
  return [...new Set(out)].join(", ");
}
